import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  Chip,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  Divider,
  Button,
  TextField,
  CircularProgress,
  Slide,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { apiClient } from "../utils/http";
import { useSession } from "../context/SessionContext";
import { useOrder } from "../context/OrderContext";
import {
  formatPrice,
  isOutOfStock,
  productMedia,
  productPrice,
  productStrikePrice,
  descriptionParagraphs,
} from "../utils/helpers";
import MediaGallery from "./MediaGallery";
import QuantityStepper from "./QuantityStepper";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// Full product detail — gallery (cover image + every product_images entry +
// an optional video_url, see utils/helpers.js productMedia), the complete
// description, options/add-ons and a quantity stepper. Opened from a tap
// anywhere on ProductCard, replacing the plain bottom-sheet the waiter app
// uses since a guest (unlike staff working the whole menu from memory) needs
// to actually see what they're ordering.
export default function ProductDetailDialog({ productId, onClose, onAdded }) {
  const { store, currency } = useSession();
  const { addItem } = useOrder();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [adding, setAdding] = useState(false);

  const [optionId, setOptionId] = useState(null);
  const [selectedAddons, setSelectedAddons] = useState({});
  const [quantity, setQuantity] = useState(1);
  const [notes, setNotes] = useState("");

  useEffect(() => {
    if (!productId) return;
    setLoading(true);
    setError("");
    setProduct(null);
    // GET v1/products/view/{id} — ProductController@productDetails — the only
    // endpoint that returns product_images/video_url/full description; the
    // list endpoint's cards deliberately don't carry all of that.
    apiClient
      .get(`products/view/${productId}`)
      .then((res) => setProduct(res.data?.product))
      .catch((err) => setError(err.message || "Failed to load this item"))
      .finally(() => setLoading(false));
  }, [productId]);

  useEffect(() => {
    if (!product) return;
    const options = product.product_options || [];
    const addons = product.addons || [];
    const defaultOption = options.find((o) => o.selected_default) || options[0] || null;
    setOptionId(defaultOption ? defaultOption.id : null);
    const defaultAddons = {};
    addons.forEach((a) => {
      if (a.pivot?.selected_default) defaultAddons[a.id] = true;
    });
    setSelectedAddons(defaultAddons);
    setQuantity(1);
    setNotes("");
  }, [product]);

  const media = useMemo(() => productMedia(product, store?.id), [product, store?.id]);
  const options = product?.product_options || [];
  const addons = product?.addons || [];
  const selectedOption = options.find((o) => o.id === optionId) || null;
  const paragraphs = useMemo(() => descriptionParagraphs(product?.description), [product]);

  const unitPrice =
    (selectedOption ? Number(selectedOption.price) : productPrice(product)) +
    Object.keys(selectedAddons)
      .filter((id) => selectedAddons[id])
      .reduce((sum, id) => {
        const addon = addons.find((a) => a.id === Number(id));
        return sum + Number(addon?.pivot?.price ?? addon?.price ?? 0);
      }, 0);

  const strike = productStrikePrice(product);
  const outOfStock = isOutOfStock(product);

  const toggleAddon = (addon) => {
    setSelectedAddons((prev) => ({ ...prev, [addon.id]: !prev[addon.id] }));
  };

  const handleAdd = async () => {
    setAdding(true);
    setError("");
    try {
      const chosenAddons = Object.keys(selectedAddons)
        .filter((id) => selectedAddons[id])
        .map((id) => ({ id: Number(id), quantity: 1 }));
      await addItem({
        product,
        option: selectedOption ? { id: selectedOption.id, price: selectedOption.price } : null,
        addons: chosenAddons,
        quantity,
        notes,
      });
      onAdded?.();
      onClose();
    } catch (err) {
      setError(err.message || "Failed to add item");
    } finally {
      setAdding(false);
    }
  };

  return (
    <Dialog fullScreen open={!!productId} onClose={onClose} TransitionComponent={Transition}>
      <AppBar position="sticky" color="default" elevation={0} sx={{ bgcolor: "#fff", borderBottom: "1px solid", borderColor: "divider" }}>
        <Toolbar>
          <Typography variant="h6" fontWeight={700} sx={{ flex: 1 }} noWrap>
            {product?.name || "Item details"}
          </Typography>
          <IconButton edge="end" onClick={onClose}>
            <Close />
          </IconButton>
        </Toolbar>
      </AppBar>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      ) : product ? (
        <Box sx={{ pb: 12 }}>
          <MediaGallery slides={media} />

          <Box sx={{ p: 2 }}>
            {product.offer_label && <Chip label={product.offer_label} size="small" color="success" sx={{ mb: 1 }} />}
            <Typography variant="h6" fontWeight={800}>
              {product.name}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, mt: 0.5, mb: 1.5 }}>
              <Typography variant="subtitle1" fontWeight={700} color="primary.main">
                {formatPrice(productPrice(product), currency)}
              </Typography>
              {strike != null && (
                <Typography variant="body2" color="text.disabled" sx={{ textDecoration: "line-through" }}>
                  {formatPrice(strike, currency)}
                </Typography>
              )}
            </Box>

            {paragraphs.length > 0 && (
              <Box sx={{ mb: 1 }}>
                {paragraphs.map((line, i) => (
                  <Typography key={i} variant="body2" color="text.secondary" sx={{ mb: 0.75 }}>
                    {line}
                  </Typography>
                ))}
              </Box>
            )}

            {options.length > 0 && (
              <>
                <Divider sx={{ my: 1.5 }} />
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                  Choose an option
                </Typography>
                <RadioGroup value={optionId} onChange={(e) => setOptionId(Number(e.target.value))}>
                  {options.map((o) => (
                    <FormControlLabel
                      key={o.id}
                      value={o.id}
                      control={<Radio size="small" />}
                      label={
                        <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%", minWidth: 220 }}>
                          <span>{o.name}</span>
                          <Typography variant="body2" color="text.secondary">
                            {formatPrice(o.price, currency)}
                          </Typography>
                        </Box>
                      }
                      sx={{ width: "100%", mr: 0 }}
                    />
                  ))}
                </RadioGroup>
              </>
            )}

            {addons.length > 0 && (
              <>
                <Divider sx={{ my: 1.5 }} />
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                  Add-ons
                </Typography>
                {addons.map((a) => (
                  <FormControlLabel
                    key={a.id}
                    control={<Checkbox size="small" checked={!!selectedAddons[a.id]} onChange={() => toggleAddon(a)} />}
                    label={
                      <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%", minWidth: 220 }}>
                        <span>{a.name}</span>
                        <Typography variant="body2" color="text.secondary">
                          +{formatPrice(a.pivot?.price ?? a.price, currency)}
                        </Typography>
                      </Box>
                    }
                    sx={{ width: "100%", mr: 0 }}
                  />
                ))}
              </>
            )}

            <Divider sx={{ my: 1.5 }} />
            <TextField
              label="Notes for the kitchen (optional)"
              fullWidth
              size="small"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              multiline
              minRows={1}
            />

            {error && (
              <Typography color="error" variant="body2" sx={{ mt: 1.5 }}>
                {error}
              </Typography>
            )}
          </Box>
        </Box>
      ) : (
        <Box sx={{ p: 3 }}>
          <Typography color="error">{error || "Item not found"}</Typography>
        </Box>
      )}

      {product && (
        <Box sx={{ position: "fixed", left: 0, right: 0, bottom: 0, bgcolor: "#fff", borderTop: "1px solid", borderColor: "divider", p: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <QuantityStepper value={quantity} onChange={(q) => setQuantity(Math.max(1, q))} />
            <Button variant="contained" size="large" disabled={adding || outOfStock} onClick={handleAdd} sx={{ px: 4 }}>
              {outOfStock
                ? "Out of stock"
                : adding
                ? "Adding..."
                : `Add · ${formatPrice(unitPrice * quantity, currency)}`}
            </Button>
          </Box>
        </Box>
      )}
    </Dialog>
  );
}
